import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useCapabilities } from '../context/CapabilityContext';
import { FeatureBoundary } from './FeatureRoute';
import CapabilityForbidden from '../jsx/pages/CapabilityForbidden';

// Role/scope authorization is separate from the commercial feature boundary.
// Reads must opt in to historical access; write routes require active access.
export const CapabilityRoute = ({
  component: Component,
  capability,
  requireRole,
  feature,
  module: blockedModule,
  redirectTo,
  intent = 'write',
  ...rest
}) => {
  const { capabilities, hasLoaded, hasScope } = useCapabilities();


  return (
    <Route
      {...rest}
      render={(props) => {
        // Resolve role data before mounting the commercial boundary.
        if (!hasLoaded) return null;
        const granted = (() => {
          if (capability) {
            // capability = "time.view.team" → ['time', 'view', 'team']
            const [mod, action, level] = capability.split('.');
            return hasScope(mod, action, level);
          }
          if (requireRole) {
            return Boolean(capabilities[requireRole]);
          }
          // No gate specified → admit. Logged so misconfigurations surface.
          // eslint-disable-next-line no-console
          console.warn('<CapabilityRoute> missing required prop: capability or requireRole');
          return true;
        })();

        if (granted) return <FeatureBoundary feature={feature} intent={intent}>{access => <Component {...props} {...access}/>}</FeatureBoundary>;

        // Phase 1.6 telemetry — capability-route 403. Console.info matches
        // the existing structured-prefix pattern (`[event-name] k=v ...`)
        // so SREs can grep + tally in browser-log aggregation. Real metrics
        // backend can replace this later. Renders only when blocked, so
        // the line is the rate of capability denials per route — should be
        // near-zero post-Phase-2.0 sidebar gating; nonzero indicates
        // bookmark/typed-URL hits or a FE-state bug.
        // eslint-disable-next-line no-console
        console.info(
          `[capability-route-403] route=${rest.path || ''} capability=${capability || ''} requireRole=${requireRole || ''} module=${blockedModule || ''}`,
        );

        if (redirectTo) {
          // Allow `:companyId` template substitution so callers can keep
          // the prop static across tenant switches.
          const companyId = props.match?.params?.companyId;
          const target = companyId ? redirectTo.replace(':companyId', companyId) : redirectTo;
          return <FeatureBoundary feature={feature} intent={intent}><Redirect to={{ pathname: target, state: { capabilityBlocked: capability || requireRole, from: props.location } }} /></FeatureBoundary>;
        }

        // Default — render the contextual 403 page.
        const moduleForCopy = blockedModule || (capability ? capability.split('.')[0] : null);
        return <FeatureBoundary feature={feature} intent={intent}><CapabilityForbidden module={moduleForCopy} /></FeatureBoundary>;
      }}
    />
  );
};

export default CapabilityRoute;
